'use client'

import Link from 'next/link'
import { createContext, forwardRef, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@lib/cn'

type SlotState = {
  target: HTMLElement | null
  setTarget: (target: HTMLElement | null) => void
}

const MobileActionContext = createContext<SlotState>({ target: null, setTarget: () => {} })

export const MobileActionProvider = ({ children }: { children: ReactNode }) => {
  const [target, setTarget] = useState<HTMLElement | null>(null)
  return <MobileActionContext.Provider value={{ target, setTarget }}>{children}</MobileActionContext.Provider>
}

export const MobileActionSlot = () => {
  const { setTarget } = useContext(MobileActionContext)
  return <div ref={setTarget} className="flex shrink-0 empty:hidden" />
}

export const MobileAction = ({ children }: { children: ReactNode }) => {
  const { target } = useContext(MobileActionContext)
  return target ? createPortal(children, target) : null
}

type MobileActionButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> & {
  label: string
  variant?: 'primary' | 'secondary'
  href?: string
  children: ReactNode
}

const buttonClass = (variant: MobileActionButtonProps['variant']) =>
  cn(
    'flex size-[38px] shrink-0 items-center justify-center rounded-full outline-hidden',
    variant === 'primary' ? 'bg-o-inverse text-o-on-inverse' : 'border border-o-line-strong bg-o-card text-o-ink'
  )

export const MobileActionButton = forwardRef<HTMLButtonElement, MobileActionButtonProps>(
  ({ label, variant = 'primary', href, onClick, children, className, ...props }, ref) => {
    if (href) {
      return (
        <Link href={href} aria-label={label} className={cn(buttonClass(variant), className)}>
          {children}
        </Link>
      )
    }
    return (
      <button ref={ref} type="button" aria-label={label} onClick={onClick} className={cn(buttonClass(variant), className)} {...props}>
        {children}
      </button>
    )
  }
)
MobileActionButton.displayName = 'MobileActionButton'

export const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden>
    <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
)
